import _ from 'lodash'
import PCrypto from '@/util/crypto/PCrypto'
import { Unencrypted, Encrypted, RUnencrypted, REncrypted } from '@/types/crypto'
import { Arrayable } from '@/types/general'
import { isRecursiveEntity } from '@/typecheck'
import { AppLockedError } from '../AppLockedError'
import Locker from '../Locker'

/**
 * Attempt to encrypt a single entry.
 *
 * @param {Unencrypted} entry
 */
const attemptEncrypt = (entry: Unencrypted): Encrypted => {
    try {
        if (!entry.pivot) {
            entry.pivot = {
                permission: 'creator'
            }
        }

        const { content, key } = PCrypto.encryptContent(
            entry.content,
            entry.pivot.documentKey
        )
        entry.pivot.documentKey = key
        return {
            ...entry,
            content
        } as Encrypted
    } catch (err) {
        console.error(err)
        throw new Error(`Failed to encrypt content for ${entry.id}: '${entry.title}'`)
    }
}

/**
 * Encrypt either:
 *  A single entry (or recursive entry)
 *  An array of entries (or recursive entries)
 *
 * @param {Arrayable<RUnencrypted>} data
 * @returns {Arrayable<REncrypted>}
 */
export const encrypt = (data: Arrayable<RUnencrypted>): Arrayable<REncrypted> => {
    if (_.isArray(data)) {
        return _.map(data, (entry: RUnencrypted) => encrypt(entry) as REncrypted)
    }
    const encrypted = attemptEncrypt(data) as REncrypted
    if (isRecursiveEntity(data)) {
        encrypted.fullRecursiveChildren = _.map(
            data.fullRecursiveChildren,
            (entry: RUnencrypted) => encrypt(entry) as REncrypted
        )
        encrypted.fullLogins = _.map(data.fullLogins, attemptEncrypt)
    }
    return encrypted
}

/**
 * Attempt to decrypt a single entry.
 *
 * @param {Encrypted} entry
 */
const attemptDecrypt = (entry: Encrypted): Unencrypted => {
    try {
        if (!entry.pivot.documentKey) {
            throw new Error('Document key missing')
        }
        const decryptedContent = PCrypto.decryptContent(
            entry.content,
            entry.pivot.documentKey
        )
        return {
            ...entry,
            content: decryptedContent
        } as Unencrypted
    } catch (err) {
        if (err instanceof AppLockedError) {
            throw err
        }
        throw new Error(`Failed to decrypt content for ${entry.id}: '${entry.title}'`)
    }
}

/**
 * Decrypt either:
 *  A single entry (or recursive entry)
 *  An array of entries (or recursive entries)
 *
 * @param {Arrayable<REncrypted>} data
 * @returns {Arrayable<RUnencrypted>}
 */
export const decrypt = (data: Arrayable<REncrypted>): Arrayable<RUnencrypted> => {
    try {
        if (_.isArray(data)) {
            return _.map(data, (entry: REncrypted) => decrypt(entry) as RUnencrypted)
        }
        const decrypted = attemptDecrypt(data) as RUnencrypted
        if (isRecursiveEntity(data)) {
            decrypted.fullRecursiveChildren = _.map(
                data.fullRecursiveChildren,
                (entry: REncrypted) => decrypt(entry) as RUnencrypted
            )
            decrypted.fullLogins = _.map(data.fullLogins, attemptDecrypt)
        }
        return decrypted
    } catch (err) {
        if (err instanceof AppLockedError) {
            Locker.lock()
        }
    }
    return []
}
